import Message from 'src/app/models/Message'
import { UnifiedMessageRequest } from '../messageApiClient'
import { buildProductRequestBody as buildUnifiedProductRequestBody } from './productMessageHelper'

// TikTok-supported wire message types. `'interactive'` covers product
// sends (PRD api-request-format.md § 5.9.4 → `interactive → product`
// subtype). Server-side adapter unpacks the envelope.
export const TIKTOK_SUPPORTED_TYPES = ['text', 'image', 'interactive'] as const
export type TiktokMessageType = typeof TIKTOK_SUPPORTED_TYPES[number]

/**
 * TiktokMessageHelper
 *
 * Platform-specific helper for TikTok messages.
 * Handles validation and request formatting specific to TikTok.
 *
 * TikTok Message Constraints:
 * - Supported types: text, image only
 * - Text length: 1-1000 characters
 * - No whitespace-only messages
 * - Image requires valid media URL
 */
export class TiktokMessageHelper {
  /**
   * Validate message type for TikTok platform
   * TikTok only supports: text, image
   */
  static validateMessageType(messageType: string): boolean {
    return TIKTOK_SUPPORTED_TYPES.includes(messageType as any)
  }

  /**
   * Get human-readable error for unsupported types
   */
  static getUnsupportedTypeError(messageType: string): string {
    return (
      `Message type '${messageType}' is not supported for TikTok. ` +
      `Supported types: ${TIKTOK_SUPPORTED_TYPES.join(', ')}`
    )
  }

  /**
   * Build TikTok-specific request body
   * Formats message data according to TikTok requirements
   */
  static buildRequestBody(
    buyerId: string,
    message: Message
  ): UnifiedMessageRequest {
    const requestBody: UnifiedMessageRequest = {
      messaging_product: 'tiktok',
      to: buyerId,
      type: message.messageType!,
    }

    // Add content based on message type
    switch (message.messageType) {
      case 'text':
        requestBody.text = {
          body: message.textContent,  // Changed from "text" to "body" to match API spec
        }
        break

      case 'image':
        if (!message.mediaUrl) {
          throw new Error('Image URL is required for image messages')
        }
        requestBody.image = {
          link: message.mediaUrl,
        }
        break

      default:
        throw new Error(this.getUnsupportedTypeError(message.messageType!))
    }

    return requestBody
  }

  /**
   * Build a PRODUCT_CARD request body (slice 1 day 3 — minimum wiring).
   *
   * Caller passes the picker-selected `Product` (only a few fields are
   * needed). The server's unified pipeline maps `type: 'product'` to
   * TikTok's `PRODUCT_CARD` message type and persists display fields
   * onto `Message.product` so the outbound card renders in chat history.
   */
  /**
   * Build the unified `interactive → product` wire body for a TikTok
   * product send.
   *
   * @deprecated Use `buildProductRequestBody` from
   *   `services/helpers/productMessageHelper.ts` directly — the wire
   *   shape is identical across TikTok and Lazada, only
   *   `messaging_product` differs. This wrapper remains for backward
   *   compatibility with existing call sites.
   */
  static buildProductRequestBody(
    buyerId: string,
    productId: string
  ): UnifiedMessageRequest {
    return buildUnifiedProductRequestBody('tiktok', buyerId, productId)
  }

  /**
   * Validate all TikTok message requirements
   * Throws error if validation fails
   */
  static validate(message: Message, buyerId: string): void {
    // Validate buyer ID
    if (!buyerId || buyerId.trim() === '') {
      throw new Error('Buyer ID is required for TikTok messages')
    }

    // Validate message type
    if (!message.messageType) {
      throw new Error('Message type is required')
    }

    if (!this.validateMessageType(message.messageType)) {
      throw new Error(this.getUnsupportedTypeError(message.messageType))
    }

    // Validate content based on type
    if (message.messageType === 'text') {
      if (!message.textContent) {
        throw new Error('Text content is required for text messages')
      }

      // Check for whitespace-only messages
      if (message.textContent.trim() === '') {
        throw new Error('Text content cannot be empty or whitespace only')
      }

      // Check length (TikTok allows up to 1000 characters)
      if (message.textContent.length > 1000) {
        throw new Error(
          `Message exceeds maximum length of 1000 characters (current: ${message.textContent.length})`
        )
      }
    }

    if (message.messageType === 'image' && !message.mediaUrl) {
      throw new Error('Media URL is required for image messages')
    }
  }
}
